/**
 * src/game/data/chapter-difficulty.ts — R-004 包 D「章節難度曲線 + 敵人差異遞增」.
 *
 * 緣由(boss 2026-06-13「關卡後面要明顯難度變高、差異變多」「關卡跟敵人都是重複的」):
 *   遊戲驅動軸 = 難度/差異隨章節遞增。本檔是該軸的**單一資料源 + 純函式**,
 *   全部 headless 可測(D4 要求「章節遞增單調性」可驗)。
 *
 * 雙軌進度鐵則(CLAUDE.md 最高優先 / wiki 鐵則 §1):
 *   難度曲線屬「玩遊戲軌(廣度)」。本檔**只**算戰鬥端難度參數(敵人 HP/攻擊/
 *   移速倍率、生成密度、波次節奏、波次組成、boss tier、通關獎勵倍率)。
 *   **絕不** import 或寫入 factionXP / freeXP / skillLevels —— 純資料 + 純函式,
 *   不碰任何 progress 寫入(learning-game-separation 守門測試覆蓋)。
 *
 * 資料驅動(D2 要求「不 inline」):每章配方放 CHAPTER_DIFFICULTY_RECIPES,
 *   可被 public/data/balance/chapters.json 覆蓋(chapter-difficulty-loader.ts)。
 *   battle scene 只透過本檔的純函式取「有效難度」,不自行 inline 倍率。
 *
 * 對應關係(stage → chapter):闖關線性 stage 編號連續遞增(ch1=1..6、
 *   ch2=7..12、ch3=13..18),本檔以 STAGES_PER_CHAPTER 推回 chapter 與「章內位置」,
 *   讓「同章後段也有梯度」(D2)。
 */
import type { EnemyVariant } from '../config';

/** 每章關卡數(5 普通 + 1 boss)。stage 編號連續,用來把 stage 映回章節。 */
export const STAGES_PER_CHAPTER = 6;

/** 目前開放的章節數(D1:開到第 3 章)。 */
export const MAX_CHAPTER = 3;

/**
 * 一章的難度配方(資料驅動,不 inline)。倍率以「第 1 章首關 = 1.0」為基準。
 * 「章內後段梯度」由 intraChapterStep 提供:章內第 k 關(0-based)額外乘
 * (1 + intraChapterStep * k),讓同章後面的關卡也比前面難一點(D2)。
 */
export interface ChapterDifficultyRecipe {
  /** 章節編號(1-based)。 */
  chapter: number;
  /** 敵人 HP 倍率(章基準)。 */
  enemyHpMult: number;
  /** 敵人攻擊(碰撞傷害 + 子彈傷害)倍率(章基準)。 */
  enemyAttackMult: number;
  /** 敵人移速倍率(章基準)。 */
  enemySpeedMult: number;
  /** 生成密度倍率(每波敵人數 × 此值,向上取整)。 */
  spawnDensityMult: number;
  /**
   * 波次節奏倍率(< 1 = 更快;wave_gap_ms × 此值)。章越後越緊湊。
   */
  waveTempoMult: number;
  /** 章內後段梯度:章內第 k 關(0-based)額外 ×(1 + intraChapterStep × k)。 */
  intraChapterStep: number;
  /** 該章關底 boss 使用的 tier(ch1=1、ch2=2、ch3=3)。 */
  bossTier: 1 | 2 | 3;
  /** 通關獎勵(指揮官 XP)倍率,隨章節遞增(D5)。 */
  rewardMult: number;
}

/**
 * 三章難度配方(資料驅動的權威預設)。設計準則(K-3 友善但「後面明顯變難」):
 *   - ch1:基準(1.0)入門,tier1 boss。
 *   - ch2:HP/攻擊/移速/密度全面 +,節奏縮短,tier2 boss。
 *   - ch3:再上一階,tier3(期中/期末)boss,獎勵最高。
 * 每章倍率「單調遞增」(HP/攻擊/密度/獎勵越後越大;tempo 越後越小=越快),
 * 故 effectiveDifficulty(下方)在章與章之間嚴格遞增(D4 單調性測試覆蓋)。
 *
 * 「敵人要多」梯度(boss 2026-06-13):spawnDensityMult 拉開 ch1→ch3 落差,
 * 讓後段章節「明顯更多敵人」。**取捨說明(整場無 enemy object pool!)**:
 *   每波量 = wave_sizes × spawnDensityMult × 章內梯度(向上取整);本次同步把
 *   spawning.json 對齊到較密的權威基準(wave_sizes [8,12,16]→[12,18,26]、
 *   spawn_interval_ms 700→450,先前 JSON 落後 TS fallback 反而較稀疏)。為避免
 *   中階手機掉幀,密度梯度刻意「線性漸增」而非爆量:ch1 1.0(溫和)→ ch2 1.5 →
 *   ch3 2.0;再疊章內梯度(intraChapterStep)後 ch3 末關約 ×2.9。以 base
 *   [12,18,26] 估,ch3 末關每波峰值 ≈ [35,53,76] 隻,但靠 spawn_interval_ms(450)
 *   把出場攤開,同屏不會一次全湧出。**再往上加密度前須先做 enemy object pool**
 *   (列為建議後續),否則可能掉幀 —— 故此處到 ch3 即守住 2.0 上限,不再加碼。
 */
export const CHAPTER_DIFFICULTY_RECIPES: readonly ChapterDifficultyRecipe[] = [
  {
    // boss 2026-06-20「前面關卡敵人數量要少一點、血量少一點」: 第 1 章入門更友善
    // (HP 1.0→0.65、攻擊 1.0→0.85、密度 1.0→0.55)。ch2/ch3 不動 → 難度梯度與單調性保留。
    chapter: 1,
    enemyHpMult: 0.65,
    enemyAttackMult: 0.85,
    enemySpeedMult: 1.0,
    spawnDensityMult: 0.55,
    waveTempoMult: 1.0,
    intraChapterStep: 0.06,
    bossTier: 1,
    rewardMult: 1.0,
  },
  {
    chapter: 2,
    enemyHpMult: 1.5,
    enemyAttackMult: 1.3,
    enemySpeedMult: 1.15,
    spawnDensityMult: 1.5,
    waveTempoMult: 0.85,
    intraChapterStep: 0.08,
    bossTier: 2,
    rewardMult: 1.6,
  },
  {
    chapter: 3,
    enemyHpMult: 2.2,
    enemyAttackMult: 1.7,
    enemySpeedMult: 1.3,
    spawnDensityMult: 2.0,
    waveTempoMult: 0.7,
    intraChapterStep: 0.1,
    bossTier: 3,
    rewardMult: 2.4,
  },
];

/**
 * 一波的敵種組成配方(用「既有敵種」做差異,D3)。weights 是相對權重
 * (scene 的 _pickVariant 可據此挑變體);kind 標註特殊波讓玩起來感覺不同。
 *   - 'mixed'  混編波(預設):normal/fast/heavy 依 weights,三種都有。
 *   - 'rush'   衝鋒波:fast 為主、夾少量 normal,密度感快。
 *   - 'tank'   坦克波:heavy 為主,硬而慢。
 *   - 'swarm'  群湧波(新):幾乎全 fast 的大量快敵,壓迫閃避(後章才出)。
 *   - 'heavy'  壓陣波(新):heavy 為核心 + 少量 normal/fast 夾擊,硬牆推進(後章才出)。
 *
 * 註:kind 只是「給人讀 / 之後若要做特效掛鉤」的標籤;scene 的 _pickVariant
 * **只依 weights 挑變體**,不讀 kind —— 故新增 kind 不需改 battle scene。
 */
export type WaveKind = 'mixed' | 'rush' | 'tank' | 'swarm' | 'heavy';

export interface WaveCompositionRecipe {
  kind: WaveKind;
  /** 相對權重(normal/fast/heavy),scene 正規化後挑變體。 */
  weights: { normal: number; fast: number; heavy: number };
}

/**
 * 每章的波次組成序列(D3:不同章/關有不同敵種組合、出場比例、特殊波)。
 * 索引 = 章內波次(0-based)。長度不必等於波數;scene 以「波 index % 序列長」
 * 取用,確保任意波數都有配方。
 *
 * 「敵人要複雜」擴充(boss 2026-06-13「敵人要多且複雜」):
 *   - 序列加長(ch1=3 → ch2=4 → ch3=5),後章波型循環更長、組成更不重複。
 *   - 後章加入「混編三色波」(normal+fast+heavy 同波)讓單波內就很雜。
 *   - 新增特殊波型:'swarm'(群湧=大量 fast)、'heavy'(壓陣=heavy 為核心)。
 *   - 設計守門:ch1 仍是「漸進引入快敵→坦克」的入門曲線(不放 swarm/壓陣);
 *     特殊波只在 ch2+ 出現,且 ch3 比 ch2 更兇、種類更多(難度單調遞增不破)。
 */
export const CHAPTER_WAVE_COMPOSITIONS: Readonly<Record<number, readonly WaveCompositionRecipe[]>> = {
  1: [
    { kind: 'mixed', weights: { normal: 1.0, fast: 0.0, heavy: 0.0 } }, // 入門純 normal
    { kind: 'mixed', weights: { normal: 0.6, fast: 0.4, heavy: 0.0 } }, // 教躲快敵
    { kind: 'mixed', weights: { normal: 0.5, fast: 0.3, heavy: 0.2 } }, // 引入坦克(三色)
  ],
  2: [
    { kind: 'rush',  weights: { normal: 0.3, fast: 0.7, heavy: 0.0 } }, // 衝鋒波(快)
    { kind: 'mixed', weights: { normal: 0.4, fast: 0.4, heavy: 0.2 } }, // 三色混編
    { kind: 'swarm', weights: { normal: 0.1, fast: 0.9, heavy: 0.0 } }, // 群湧波(大量快敵)
    { kind: 'tank',  weights: { normal: 0.2, fast: 0.2, heavy: 0.6 } }, // 坦克波(硬)
  ],
  3: [
    { kind: 'rush',  weights: { normal: 0.2, fast: 0.8, heavy: 0.0 } }, // 更兇衝鋒
    { kind: 'mixed', weights: { normal: 0.3, fast: 0.4, heavy: 0.3 } }, // 全混編(三色均衡)
    { kind: 'swarm', weights: { normal: 0.05, fast: 0.95, heavy: 0.0 } }, // 極限群湧
    { kind: 'heavy', weights: { normal: 0.2, fast: 0.2, heavy: 0.6 } }, // 壓陣波(heavy 核心 + 夾擊)
    { kind: 'tank',  weights: { normal: 0.1, fast: 0.2, heavy: 0.7 } }, // 重坦克波
  ],
};

/** stage 編號(1-based)→ 章節編號(1-based)。clamp 在 [1, MAX_CHAPTER]。 */
export function chapterForStage(stage: number): number {
  const s = Number.isFinite(stage) && stage >= 1 ? Math.floor(stage) : 1;
  const ch = Math.floor((s - 1) / STAGES_PER_CHAPTER) + 1;
  return Math.max(1, Math.min(MAX_CHAPTER, ch));
}

/** stage 編號(1-based)→ 章內位置(0-based,0 = 該章首關)。 */
export function indexWithinChapter(stage: number): number {
  const s = Number.isFinite(stage) && stage >= 1 ? Math.floor(stage) : 1;
  const idx = (s - 1) % STAGES_PER_CHAPTER;
  return Math.max(0, idx);
}

/** 取某章配方;超出範圍 clamp 到最近的有效章。 */
export function recipeForChapter(
  chapter: number,
  recipes: readonly ChapterDifficultyRecipe[] = CHAPTER_DIFFICULTY_RECIPES,
): ChapterDifficultyRecipe {
  const ch = Math.max(1, Math.min(recipes.length, Math.floor(chapter) || 1));
  return recipes[ch - 1];
}

/**
 * 某 stage 的「有效難度倍率組」:章基準 × 章內梯度。
 * battle scene 用這組倍率把基礎 balance(HP/攻擊/移速/密度/節奏)放大。
 * 純資料 → 不寫任何戰力。
 */
export interface EffectiveDifficulty {
  chapter: number;
  /** 章內位置(0-based)。 */
  stageInChapter: number;
  enemyHpMult: number;
  enemyAttackMult: number;
  enemySpeedMult: number;
  spawnDensityMult: number;
  waveTempoMult: number;
  bossTier: 1 | 2 | 3;
  rewardMult: number;
}

export function effectiveDifficulty(
  stage: number,
  recipes: readonly ChapterDifficultyRecipe[] = CHAPTER_DIFFICULTY_RECIPES,
): EffectiveDifficulty {
  const chapter = chapterForStage(stage);
  const k = indexWithinChapter(stage);
  const r = recipeForChapter(chapter, recipes);
  // 章內梯度:HP/攻擊/密度 隨 k 上升;節奏隨 k 下降(更快)。
  const grad = 1 + r.intraChapterStep * k;
  return {
    chapter,
    stageInChapter: k,
    enemyHpMult: r.enemyHpMult * grad,
    enemyAttackMult: r.enemyAttackMult * grad,
    enemySpeedMult: r.enemySpeedMult * (1 + r.intraChapterStep * 0.5 * k),
    spawnDensityMult: r.spawnDensityMult * grad,
    // 節奏:章內後段更緊湊(gap 更短),下限 0.5 避免過快。
    waveTempoMult: Math.max(0.5, r.waveTempoMult * (1 - r.intraChapterStep * 0.5 * k)),
    bossTier: r.bossTier,
    rewardMult: r.rewardMult * grad,
  };
}

/**
 * 標量「難度分數」:把有效難度組壓成單一可比數值,給 D4 單調性測試與除錯用。
 * 各分量乘權重後相加(節奏越快 = 1/tempo 越大 = 越難)。
 * 設計保證:同章後段 > 前段;章與章之間嚴格遞增(配方各分量單調)。
 */
export function difficultyScore(
  stage: number,
  recipes: readonly ChapterDifficultyRecipe[] = CHAPTER_DIFFICULTY_RECIPES,
): number {
  const d = effectiveDifficulty(stage, recipes);
  return (
    d.enemyHpMult * 1.0 +
    d.enemyAttackMult * 1.0 +
    d.enemySpeedMult * 0.5 +
    d.spawnDensityMult * 0.8 +
    (1 / d.waveTempoMult) * 0.5 +
    d.bossTier * 0.5
  );
}

/**
 * 取某 stage 對應的「該波組成配方」。波 index(0-based)在章配方序列裡循環取用。
 * D3:不同章/關有不同敵種組合與特殊波。
 */
export function waveCompositionFor(
  stage: number,
  waveIndex: number,
): WaveCompositionRecipe {
  const chapter = chapterForStage(stage);
  const seq = CHAPTER_WAVE_COMPOSITIONS[chapter] ?? CHAPTER_WAVE_COMPOSITIONS[1];
  // 防呆:waveIndex 非有限(NaN/undefined,測試略過 init)→ 視為 0。
  const w = Number.isFinite(waveIndex) ? Math.floor(waveIndex) : 0;
  const i = ((w % seq.length) + seq.length) % seq.length;
  return seq[i];
}

/**
 * 從一個組成配方挑一個既有敵種變體(純函式,seed ∈ [0,1))。
 * weights 自動正規化;全 0 → 'normal'。scene 的 _pickVariant 可改用此函式。
 */
export function pickVariantFromComposition(
  recipe: WaveCompositionRecipe,
  seed: number,
): EnemyVariant {
  const { normal, fast, heavy } = recipe.weights;
  const total = normal + fast + heavy;
  if (total <= 0) return 'normal';
  const t = (Number.isFinite(seed) ? Math.max(0, Math.min(0.999999, seed)) : 0) * total;
  if (t < normal) return 'normal';
  if (t < normal + fast) return 'fast';
  return 'heavy';
}

/**
 * 通關獎勵(指揮官 XP)倍率(D5):隨章節遞增。battle scene / result-scene 把
 * computeCommanderXp 的結果乘上此倍率(只放大「玩遊戲軌」廣度獎勵,**不**碰戰力)。
 * 純函式,不寫任何 progress。
 */
export function rewardMultiplierForStage(
  stage: number,
  recipes: readonly ChapterDifficultyRecipe[] = CHAPTER_DIFFICULTY_RECIPES,
): number {
  return effectiveDifficulty(stage, recipes).rewardMult;
}
